import { useState, useEffect } from 'react';
import { Eye, Check, X, FileText, Download, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';
import { usePermissions } from '@/hooks/usePermissions';
import JSZip from 'jszip';

interface DocumentoValidacao {
  id: string;
  id_usuario: string;
  nome_documento: string;
  retorno_documento: number;
  descricao_retorno?: string;
  created_at: string;
  updated_at: string;
  created_by?: string;
  updated_by?: string;
  caminho_storage?: string;
}

interface DocumentosValidacaoRHProps {
  contratacaoId: string;
  userId?: string;
  userEmail?: string;
  userCpf?: string;
}

// Função utilitária para formatar tipos de documento
const formatTipoDocumento = (tipo: string) => {
  const tipos: Record<string, string> = {
    'rg': 'RG',
    'rg_frente': 'RG - Frente',
    'rg_verso': 'RG - Verso', 
    'cpf': 'CPF',
    'comprovante_residencia': 'Comprovante de Residência',
    'carteira_trabalho': 'Carteira de Trabalho',
    'titulo_eleitoral': 'Título de Eleitor',
    'certidao_nascimento': 'Certidão de Nascimento',
    'certificado_escolar': 'Certificado Escolar',
    'certificado_reservista': 'Certificado Reservista',
    'comprovante_escolaridade': 'Comprovante de Escolaridade',
    'comprovante_bancario': 'Comprovante Bancário',
    'foto_3x4': 'Foto 3x4',
    'atestado_saude': 'Atestado de Saúde'
  };

  // Tratar documentos de dependentes (ex: dependente_1_certidao_nascimento)
  const depMatch = tipo.match(/^dependente_(\d+)_(.+)$/);
  if (depMatch) {
    const numDep = depMatch[1];
    const tipoDoc = depMatch[2];
    const tipoFormatado = tipos[tipoDoc] || tipoDoc.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    return `Dependente ${numDep} - ${tipoFormatado}`;
  }

  return tipos[tipo] || tipo;
};

// Função para encontrar o arquivo correto no bucket baseado no nome da tabela
const encontrarArquivoNoBucket = (nomeDocumento: string, arquivosBucket: any[]) => {
  console.log('=== INÍCIO DA BUSCA ===');
  console.log('Buscando documento:', nomeDocumento);
  console.log('Arquivos disponíveis no bucket:', arquivosBucket.map(f => f.name));
  
  // Primeiro, tentar encontrar exato
  let arquivo = arquivosBucket.find(file => file.name === nomeDocumento);
  if (arquivo) {
    console.log('✅ Encontrado por nome exato:', arquivo.name);
    return arquivo.name;
  }

  // Mapeamento por padrões de nome (sem extensão fixa)
  const mapeamentoPadrao: { [key: string]: string } = {
    // Carteira de Trabalho
    'carteira_trabalho': 'carteira de trabalho',
    'carteira_de_trabalho': 'carteira de trabalho',
    
    // Certidão de Nascimento  
    'certidao_nascimento': 'certidao de nascimento',
    'certidao_de_nascimento': 'certidao de nascimento', 
    'nascimento': 'certidao de nascimento',
    
    // Certificado de Reservista
    'certificado_reservista': 'certificado de reservista',
    'reservista': 'certificado de reservista',
    
    // Comprovante Bancário
    'dados_bancarios': 'comprovante bancario',
    'comprovante_bancario': 'comprovante bancario',
    
    // Comprovante de Escolaridade
    'comprovante_escolaridade': 'comprovante de escolaridade',
    'comprovante_de_escolaridade': 'comprovante de escolaridade',
    'certificado_escolar': 'comprovante de escolaridade',
    
    // Comprovante de Residência
    'comprovante_residencia': 'comprovante de residencia',
    'comprovante_de_residencia': 'comprovante de residencia',
    'residencia': 'comprovante de residencia',
    
    // Foto 3x4
    'foto_3x4': 'foto 3x4',
    
    // Título de Eleitor
    'titulo_eleitoral': 'titulo de eleitor',
    'titulo_de_eleitor': 'titulo de eleitor',
    
    // Atestado de Saúde
    'atestado_saude': 'atestado'
  };

  // Normalizar nome para busca
  const nomeNormalizado = nomeDocumento.toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[áàâã]/g, 'a')
    .replace(/[éêë]/g, 'e')
    .replace(/[íî]/g, 'i')
    .replace(/[óôõ]/g, 'o')
    .replace(/[úû]/g, 'u')
    .replace(/[ç]/g, 'c');

  console.log('Nome original:', nomeDocumento);
  console.log('Nome normalizado:', nomeNormalizado);
  console.log('Mapeamento disponível:', Object.keys(mapeamentoPadrao));

  // Buscar pelo mapeamento de padrão
  if (mapeamentoPadrao[nomeNormalizado]) {
    const padraoNome = mapeamentoPadrao[nomeNormalizado];
    console.log('✅ Encontrado mapeamento para:', nomeNormalizado, '→', padraoNome);
    
    // Buscar arquivo que contenha o padrão (sem considerar extensão)
    arquivo = arquivosBucket.find(file => 
      file.name.toLowerCase().replace(/\.[^/.]+$/, "").includes(padraoNome)
    );
    
    if (arquivo) {
      console.log('✅ Arquivo encontrado no bucket:', arquivo.name);
      return arquivo.name;
    } else {
      console.log('❌ Arquivo não encontrado no bucket para padrão:', padraoNome);
      console.log('Arquivos disponíveis para comparação:');
      arquivosBucket.forEach(f => {
        const nomeArquivoSemExtensao = f.name.toLowerCase().replace(/\.[^/.]+$/, "");
        console.log('  -', f.name, '(normalizado:', nomeArquivoSemExtensao, ')');
      });
    }
  } else {
    console.log('❌ Nenhum mapeamento encontrado para:', nomeNormalizado);
  }

  // Para o caso especial do RG genérico, buscar por diferentes extensões
  if (nomeNormalizado === 'rg') {
    // Primeiro, buscar arquivo RG simples (sem frente/verso)
    const rgSimples = arquivosBucket.find(file => 
      file.name.toLowerCase().match(/^rg\.(jpg|jpeg|png|pdf|webp)$/i)
    );
    
    if (rgSimples) {
      console.log('✅ Encontrado RG simples:', rgSimples.name);
      return rgSimples.name;
    }

    // Buscar RG Frente com diferentes extensões
    const rgFrente = arquivosBucket.find(file => 
      file.name.toLowerCase().startsWith('rg frente.') ||
      file.name.toLowerCase() === 'rg frente.jpeg' ||
      file.name.toLowerCase() === 'rg frente.jpg' ||
      file.name.toLowerCase() === 'rg frente.png'
    );
    
    // Buscar RG Verso com diferentes extensões  
    const rgVerso = arquivosBucket.find(file => 
      file.name.toLowerCase().startsWith('rg verso.') ||
      file.name.toLowerCase() === 'rg verso.jpeg' ||
      file.name.toLowerCase() === 'rg verso.jpg' ||
      file.name.toLowerCase() === 'rg verso.png'
    );
    
    if (rgFrente && rgVerso) {
      console.log('✅ Encontrados ambos RG Frente e Verso');
      console.log('Arquivo encontrado:', [rgFrente.name, rgVerso.name]);
      return [rgFrente.name, rgVerso.name];
    } else if (rgFrente) {
      console.log('✅ Encontrado apenas RG Frente:', rgFrente.name);
      return rgFrente.name;
    } else if (rgVerso) {
      console.log('✅ Encontrado apenas RG Verso:', rgVerso.name);
      return rgVerso.name;
    }
    
    console.log('❌ Nenhum arquivo RG encontrado');
  }

  console.log('❌ Arquivo não encontrado para:', nomeDocumento);
  console.log('=== FIM DA BUSCA ===');
  return null;
};

// Componente para visualizar documento do storage
interface DocumentViewerProps {
  nomeDocumento: string;
  caminhoStorage?: string;
  cpf: string;
}

function DocumentViewer({ nomeDocumento, caminhoStorage, cpf }: DocumentViewerProps) {
  const [documentUrls, setDocumentUrls] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const carregarDocumento = async () => {
      if (!cpf || !nomeDocumento) {
        console.log('CPF ou nome do documento faltando:', { cpf, nomeDocumento });
        setError(true);
        setLoading(false);
        return;
      }

      try {
        console.log('Tentando carregar documento:', nomeDocumento);
        console.log('Bucket:', 'contratacao-documentos');
        
        // Verificar se é documento de dependente (padrão dependente_N_tipo)
        const isDependente = nomeDocumento.match(/^dependente_\d+_/);
        
        if (isDependente) {
          // Para dependentes, buscar diretamente na pasta dependentes/
          const { data: depListData, error: depListError } = await supabase.storage
            .from('contratacao-documentos')
            .list(`${cpf}/dependentes`, { limit: 100 });

          if (depListError) {
            console.error('Erro ao listar arquivos de dependentes:', depListError);
            setError(true);
            setLoading(false);
            return;
          }

          // Buscar arquivo que corresponda ao nome
          const arquivoDep = depListData?.find(file => {
            const nomeBase = file.name.replace(/\.[^/.]+$/, '');
            return nomeBase.toLowerCase() === nomeDocumento.toLowerCase();
          });

          if (!arquivoDep) {
            console.error('Arquivo de dependente não encontrado:', nomeDocumento);
            setError(true);
            setLoading(false);
            return;
          }

          const filePath = `${cpf}/dependentes/${arquivoDep.name}`;
          const { data: signedUrl, error: signError } = await supabase.storage
            .from('contratacao-documentos')
            .createSignedUrl(filePath, 3600);

          if (signError) {
            console.error('Erro ao criar URL assinada:', signError);
            setError(true);
          } else {
            setDocumentUrls([signedUrl.signedUrl]);
          }
          setLoading(false);
          return;
        }
        
        // Primeiro, vamos listar os arquivos para encontrar o correto
        const { data: listData, error: listError } = await supabase.storage
          .from('contratacao-documentos')
          .list(cpf, {
            limit: 100,
            offset: 0
          });

        if (listError) {
          console.error('Erro ao listar arquivos na pasta:', listError);
          setError(true);
          setLoading(false);
          return;
        }

        console.log('Arquivos encontrados na pasta:', listData);
        
        // Encontrar o arquivo correto
        const nomeArquivoCorreto = encontrarArquivoNoBucket(nomeDocumento, listData || []);
        
        if (!nomeArquivoCorreto) {
          console.error('Arquivo não encontrado no bucket para:', nomeDocumento);
          setError(true);
          setLoading(false);
          return;
        }

        console.log('Arquivo encontrado:', nomeArquivoCorreto);
        
        // Verificar se retornou um array (múltiplos documentos como RG)
        const arquivos = Array.isArray(nomeArquivoCorreto) ? nomeArquivoCorreto : [nomeArquivoCorreto];
        const urls: string[] = [];
        
        // Carregar cada arquivo
        for (const nomeArquivo of arquivos) {
          const filePath = `${cpf}/${nomeArquivo}`;
          console.log('Carregando arquivo:', filePath);
          
          // Criar URL assinada para permitir visualização inline (igual ao sistema de contratação)
          const { data: signedUrl, error } = await supabase.storage
            .from('contratacao-documentos')
            .createSignedUrl(filePath, 3600); // URL válida por 1 hora

          if (error) {
            console.error('Erro ao criar URL assinada:', { error, filePath });
            setError(true);
            return;
          } else {
            console.log('URL assinada criada com sucesso:', filePath);
            urls.push(signedUrl.signedUrl);
          }
        }
        
        setDocumentUrls(urls);
      } catch (error) {
        console.error('Erro geral ao processar documento:', error);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    carregarDocumento();

    // Cleanup function para revogar URLs quando componente desmonta
    return () => {
      documentUrls.forEach(url => {
        URL.revokeObjectURL(url);
      });
    };
  }, [nomeDocumento, cpf]);

  if (loading) {
    return <div className="text-muted-foreground p-4 text-center">Carregando documento...</div>;
  }

  if (error || documentUrls.length === 0) {
    return <div className="text-muted-foreground p-4 text-center">Erro ao carregar documento ou documento não encontrado</div>;
  }

  return (
    <div className="space-y-4">
      {documentUrls.map((documentUrl, index) => {
        // Detectar tipo baseado no nome do documento original (mais confiável)
        const isPdf = nomeDocumento.toLowerCase().includes('.pdf') || 
                     nomeDocumento.toLowerCase().includes('pdf') ||
                     nomeDocumento.toLowerCase().includes('carteira') ||
                     nomeDocumento.toLowerCase().includes('escolaridade');
        
        // Para RG, mostrar títulos específicos
        const documentTitle = documentUrls.length > 1 
          ? `${formatTipoDocumento(nomeDocumento)} - ${index === 0 ? 'Frente' : 'Verso'}`
          : formatTipoDocumento(nomeDocumento);

        return (
          <div key={index} className="border rounded-lg p-4">
            {documentUrls.length > 1 && (
              <h5 className="font-medium mb-3 text-center">{documentTitle}</h5>
            )}
            
            <div className="flex justify-between items-center mb-3">
              <span className="text-sm text-muted-foreground">{documentTitle}</span>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => window.open(documentUrl, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Abrir
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    const link = document.createElement('a');
                    link.href = documentUrl;
                    link.download = documentTitle;
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                  }}
                >
                  <Download className="h-4 w-4 mr-1" />
                  Baixar
                </Button>
              </div>
            </div>
            
            {/* Visualização inline do documento igual ao sistema de contratação */}
            <div className="w-full h-[60vh] bg-gray-100 rounded-lg overflow-hidden">
              {isPdf ? (
                <iframe
                  src={documentUrl}
                  className="w-full h-full border-0"
                  title={`Visualização do documento PDF - ${documentTitle}`}
                />
              ) : (
                <img
                  src={documentUrl}
                  alt={documentTitle}
                  className="w-full h-full object-contain"
                  onError={(e) => {
                    console.error('Erro ao carregar imagem:', e);
                    const target = e.target as HTMLImageElement;
                    target.style.display = 'none';
                    const parent = target.parentElement;
                    if (parent) {
                      parent.innerHTML = `
                        <div class="flex items-center justify-center h-full text-gray-500">
                          <div class="text-center">
                            <svg class="h-16 w-16 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                            </svg>
                            <p>Não foi possível carregar a visualização</p>
                            <p class="text-sm">Use o botão "Baixar" para acessar o arquivo</p>
                          </div>
                        </div>
                      `;
                    }
                  }}
                />
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function DocumentosValidacaoRH({ contratacaoId, userId, userEmail, userCpf }: DocumentosValidacaoRHProps) {
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const [documentos, setDocumentos] = useState<DocumentoValidacao[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedDocument, setSelectedDocument] = useState<DocumentoValidacao | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [cpfUsuario, setCpfUsuario] = useState<string>('');
  const [processing, setProcessing] = useState(false);
  const [selectedDocuments, setSelectedDocuments] = useState<Set<string>>(new Set());
  const [downloading, setDownloading] = useState(false);
  const [resolvedUserId, setResolvedUserId] = useState<string>('');

  const canWrite = hasPermission('recursos_humanos', 'contratacao', 'write');

  // Primeiro useEffect: Resolver o userId se não estiver disponível
  useEffect(() => {
    const resolverUserId = async () => {
      // Se já temos userId, usar ele diretamente
      if (userId) {
        setResolvedUserId(userId);
        return;
      }

      // Tentar buscar pelo email
      if (userEmail) {
        console.log('Buscando userId pelo email:', userEmail);
        
        // Tentar RPC primeiro
        const { data: userIdData } = await supabase.rpc('get_user_id_by_email', {
          user_email: userEmail
        });
        
        if (userIdData) {
          console.log('UserId encontrado via RPC:', userIdData);
          setResolvedUserId(userIdData);
          return;
        }

        // Fallback: buscar no profiles
        const { data: profileData } = await supabase
          .from('profiles')
          .select('user_id')
          .eq('email', userEmail)
          .maybeSingle();
        
        if (profileData?.user_id) {
          console.log('UserId encontrado via profiles:', profileData.user_id);
          setResolvedUserId(profileData.user_id);
          return;
        }
      }

      // Tentar buscar pelo CPF
      if (userCpf) {
        console.log('Buscando userId pelo CPF:', userCpf);
        const cpfLimpo = userCpf.replace(/[^\d]/g, '');
        
        const { data: dadosData } = await supabase
          .from('dados_pessoais')
          .select('user_id')
          .eq('cpf', cpfLimpo)
          .maybeSingle();
        
        if (dadosData?.user_id) {
          console.log('UserId encontrado via CPF:', dadosData.user_id);
          setResolvedUserId(dadosData.user_id);
          return;
        }
      }

      console.log('Não foi possível resolver o userId');
    };

    resolverUserId();
  }, [userId, userEmail, userCpf]);

  // Segundo useEffect: Buscar CPF quando temos userId
  useEffect(() => {
    if (resolvedUserId) {
      buscarCpfUsuario();
    }
  }, [resolvedUserId, contratacaoId]);

  useEffect(() => {
    if (cpfUsuario) {
      carregarDocumentos();
    }
  }, [cpfUsuario, userId]);

  const carregarDocumentos = async () => {
    try {
      setLoading(true);
      
      if (!cpfUsuario || !resolvedUserId) {
        setDocumentos([]);
        return;
      }

      // Buscar documentos da tabela validacao_documentos
      const { data, error } = await supabase
        .from('validacao_documentos')
        .select('*')
        .eq('id_usuario', resolvedUserId)
        .order('created_at', { ascending: false });

      if (error) throw error;

      console.log('Documentos carregados:', data);
      
      // Buscar também documentos de dependentes no bucket
      const { data: depFiles, error: depError } = await supabase.storage
        .from('contratacao-documentos')
        .list(`${cpfUsuario}/dependentes`, { limit: 100 });

      console.log('Arquivos de dependentes no bucket:', depFiles);

      const documentosFinais: DocumentoValidacao[] = [...(data || [])];

      if (!depError && depFiles && depFiles.length > 0) {
        for (const file of depFiles) {
          if (!file.name || file.name === '.emptyFolderPlaceholder') continue;
          
          // Extrair nome sem extensão para usar como identificador
          const nomeBase = file.name.replace(/\.[^/.]+$/, '');
          
          // Verificar se já existe na tabela validacao_documentos
          const jaExiste = documentosFinais.some(d => 
            d.nome_documento.toLowerCase().replace(/\s+/g, '_') === nomeBase.toLowerCase() ||
            d.caminho_storage?.includes(file.name)
          );

          if (!jaExiste) {
            // Adicionar como documento virtual para exibição
            documentosFinais.push({
              id: `dep-${file.name}`,
              id_usuario: resolvedUserId,
              nome_documento: nomeBase,
              retorno_documento: -1, // Pendente
              created_at: file.created_at || new Date().toISOString(),
              updated_at: file.updated_at || new Date().toISOString(),
              caminho_storage: `${cpfUsuario}/dependentes/${file.name}`
            });
          }
        }
      }

      setDocumentos(documentosFinais);
    } catch (error: any) {
      toast({
        title: "Erro ao carregar documentos",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const buscarCpfUsuario = async () => {
    try {
      if (!resolvedUserId) {
        console.log('Nenhum userId resolvido, não é possível buscar CPF');
        return;
      }

      const { data, error } = await supabase
        .from('dados_pessoais')
        .select('cpf')
        .eq('user_id', resolvedUserId)
        .maybeSingle();

      if (error) throw error;
      if (data?.cpf) {
        console.log('CPF encontrado:', data.cpf);
        setCpfUsuario(data.cpf);
      } else {
        console.log('Nenhum CPF encontrado para userId:', resolvedUserId);
      }
    } catch (error: any) {
      console.error('Erro ao buscar CPF do usuário:', error);
    }
  };

  const aprovarDocumento = async (documentoId: string) => {
    if (!canWrite) {
      toast({
        title: "Sem permissão",
        description: "Você não tem permissão para aprovar documentos",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);
      const { error } = await supabase
        .from('validacao_documentos')
        .update({ 
          retorno_documento: 1,
          descricao_retorno: 'Documento aprovado',
          updated_at: new Date().toISOString()
        })
        .eq('id', documentoId);

      if (error) throw error;

      toast({
        title: "Documento aprovado",
        description: "O documento foi aprovado com sucesso",
      });

      await carregarDocumentos();
      await verificarLiberacaoParaExame();
    } catch (error: any) {
      toast({
        title: "Erro ao aprovar documento",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const reprovarDocumento = async (documentoId: string) => {
    if (!canWrite) {
      toast({
        title: "Sem permissão",
        description: "Você não tem permissão para reprovar documentos",
        variant: "destructive",
      });
      return;
    }

    if (!rejectionReason.trim()) {
      toast({
        title: "Motivo obrigatório",
        description: "Por favor, informe o motivo da reprovação",
        variant: "destructive",
      });
      return;
    }

    try {
      setProcessing(true);
      const { error } = await supabase
        .from('validacao_documentos')
        .update({ 
          retorno_documento: 0,
          descricao_retorno: rejectionReason,
          updated_at: new Date().toISOString()
        })
        .eq('id', documentoId);

      if (error) throw error;

      toast({
        title: "Documento reprovado",
        description: "O documento foi reprovado com sucesso",
      });

      setRejectionReason('');
      setSelectedDocument(null);
      await carregarDocumentos();
    } catch (error: any) {
      toast({
        title: "Erro ao reprovar documento",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setProcessing(false);
    }
  };

  const verificarLiberacaoParaExame = async () => {
    try {
      // Verificar se todos os documentos foram aprovados
      const todosAprovados = documentos.every(doc => doc.retorno_documento === 1);
      
      if (todosAprovados && documentos.length > 0) {
        // Atualizar a evolução da contratação para liberar para exame
        const { error } = await supabase
          .from('evolucao_contratacao')
          .update({
            etapa_atual: 'exame',
            validacao_status: 'concluido',
            updated_at: new Date().toISOString()
          })
          .eq('user_id', userId);

        if (error) {
          console.error('Erro ao liberar para exame:', error);
        } else {
          toast({
            title: "Liberado para exame",
            description: "Todos os documentos foram aprovados. O candidato foi liberado para a etapa de exame.",
          });
        }
      }
    } catch (error) {
      console.error('Erro ao verificar liberação para exame:', error);
    }
  };

  const getStatusBadge = (retorno: number) => {
    if (retorno === 1) {
      return <Badge className="bg-green-100 text-green-800 border-green-200">Aprovado</Badge>;
    } else if (retorno === 0) {
      return <Badge className="bg-red-100 text-red-800 border-red-200">Reprovado</Badge>;
    } else {
      return <Badge variant="outline" className="text-muted-foreground">Pendente</Badge>;
    }
  };

  // Função para alternar seleção de documento
  const toggleDocumentSelection = (documentoId: string) => {
    setSelectedDocuments(prev => {
      const newSelection = new Set(prev);
      if (newSelection.has(documentoId)) {
        newSelection.delete(documentoId);
      } else {
        newSelection.add(documentoId);
      }
      return newSelection;
    });
  };

  // Função para selecionar/deselecionar todos
  const toggleAllDocuments = () => {
    if (selectedDocuments.size === documentos.length) {
      setSelectedDocuments(new Set());
    } else {
      setSelectedDocuments(new Set(documentos.map(d => d.id)));
    }
  };

  // Função para baixar documento individual ou múltiplos em ZIP
  const downloadDocuments = async () => {
    if (!cpfUsuario) {
      toast({
        title: "Erro",
        description: "CPF do usuário não encontrado",
        variant: "destructive",
      });
      return;
    }

    setDownloading(true);

    try {
      const documentsToDownload = selectedDocuments.size > 0 
        ? documentos.filter(d => selectedDocuments.has(d.id))
        : documentos;

      if (documentsToDownload.length === 0) {
        toast({
          title: "Nenhum documento",
          description: "Não há documentos para baixar",
          variant: "destructive",
        });
        return;
      }

      if (documentsToDownload.length === 1) {
        // Download individual
        await downloadSingleDocument(documentsToDownload[0]);
      } else {
        // Download múltiplo em ZIP
        await downloadMultipleDocuments(documentsToDownload);
      }

    } catch (error: any) {
      toast({
        title: "Erro no download",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setDownloading(false);
    }
  };

  const downloadSingleDocument = async (documento: DocumentoValidacao) => {
    const { data: listData, error: listError } = await supabase.storage
      .from('contratacao-documentos')
      .list(cpfUsuario);

    if (listError) throw listError;

    const nomeArquivoCorreto = encontrarArquivoNoBucket(documento.nome_documento, listData || []);
    if (!nomeArquivoCorreto) {
      throw new Error('Arquivo não encontrado');
    }

    const arquivos = Array.isArray(nomeArquivoCorreto) ? nomeArquivoCorreto : [nomeArquivoCorreto];
    
    for (const nomeArquivo of arquivos) {
      const filePath = `${cpfUsuario}/${nomeArquivo}`;
      
      const { data, error } = await supabase.storage
        .from('contratacao-documentos')
        .download(filePath);

      if (error) throw error;

      const url = URL.createObjectURL(data);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${formatTipoDocumento(documento.nome_documento)} - ${nomeArquivo}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const downloadMultipleDocuments = async (documentos: DocumentoValidacao[]) => {
    const zip = new JSZip();
    
    // Buscar arquivos do storage
    const { data: listData, error: listError } = await supabase.storage
      .from('contratacao-documentos')
      .list(cpfUsuario);

    if (listError) throw listError;

    for (const documento of documentos) {
      try {
        const nomeArquivoCorreto = encontrarArquivoNoBucket(documento.nome_documento, listData || []);
        if (!nomeArquivoCorreto) continue;

        const arquivos = Array.isArray(nomeArquivoCorreto) ? nomeArquivoCorreto : [nomeArquivoCorreto];
        
        for (const nomeArquivo of arquivos) {
          const filePath = `${cpfUsuario}/${nomeArquivo}`;
          
          const { data, error } = await supabase.storage
            .from('contratacao-documentos')
            .download(filePath);

          if (!error && data) {
            const fileName = `${formatTipoDocumento(documento.nome_documento)} - ${nomeArquivo}`;
            zip.file(fileName, data);
          }
        }
      } catch (error) {
        console.error(`Erro ao processar documento ${documento.nome_documento}:`, error);
        continue;
      }
    }

    // Gerar e baixar o ZIP
    const zipBlob = await zip.generateAsync({ type: 'blob' });
    const url = URL.createObjectURL(zipBlob);
    const link = document.createElement('a');
    link.href = url;
    
    // Nome do arquivo ZIP com o CPF do candidato
    const nomeCandidate = documentos.length > 0 ? cpfUsuario : 'documentos';
    link.download = `Documentos_${nomeCandidate}_${new Date().toISOString().split('T')[0]}.zip`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast({
      title: "Download concluído",
      description: `${documentos.length} documento(s) baixado(s) com sucesso`,
    });
  };

  if (loading) {
    return <div className="flex justify-center py-8">Carregando documentos...</div>;
  }

  if (documentos.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
        <p>Nenhum documento encontrado para validação</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h4 className="font-medium text-lg">Documentos para Validação ({documentos.length})</h4>
        <div className="flex items-center gap-4">
          {selectedDocuments.size > 0 && (
            <Badge variant="secondary">
              {selectedDocuments.size} selecionado(s)
            </Badge>
          )}
          <Badge variant="outline">
            {documentos.filter(d => d.retorno_documento === 1).length} aprovados de {documentos.length}
          </Badge>
        </div>
      </div>

      {/* Controles de seleção e download */}
      <div className="flex justify-between items-center p-3 bg-muted/30 rounded-lg">
        <div className="flex items-center gap-2">
          <Checkbox
            id="select-all"
            checked={selectedDocuments.size === documentos.length && documentos.length > 0}
            onCheckedChange={toggleAllDocuments}
          />
          <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
            Selecionar Todos
          </label>
        </div>
        
        <Button 
          onClick={downloadDocuments}
          disabled={downloading}
          className="gap-2"
        >
          <Download className="h-4 w-4" />
          {downloading ? 'Baixando...' : selectedDocuments.size > 0 ? `Baixar (${selectedDocuments.size})` : 'Baixar Todos'}
        </Button>
      </div>

      <div className="space-y-3">
        {documentos.map((documento) => (
          <Card key={documento.id} className="hover:shadow-md transition-shadow">
            <CardContent className="p-4">
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-3 flex-1">
                  <Checkbox
                    checked={selectedDocuments.has(documento.id)}
                    onCheckedChange={() => toggleDocumentSelection(documento.id)}
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <FileText className="h-4 w-4 text-muted-foreground" />
                      <h5 className="font-medium">{formatTipoDocumento(documento.nome_documento)}</h5>
                      {getStatusBadge(documento.retorno_documento)}
                    </div>
                    
                    <p className="text-xs text-muted-foreground mb-2">
                      Enviado em: {new Date(documento.created_at).toLocaleString('pt-BR')}
                    </p>

                    {documento.descricao_retorno && (
                      <div className="mt-2 p-2 bg-muted/50 rounded text-sm">
                        <strong>Observação:</strong> {documento.descricao_retorno}
                      </div>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 ml-4">
                  {/* Botão Visualizar */}
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button variant="outline" size="sm">
                        <Eye className="h-4 w-4 mr-1" />
                        Visualizar
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle className="flex items-center gap-2">
                          <FileText className="h-4 w-4" />
                          {formatTipoDocumento(documento.nome_documento)}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4">
                        {cpfUsuario ? (
                          <DocumentViewer 
                            nomeDocumento={documento.nome_documento} 
                            caminhoStorage={documento.caminho_storage}
                            cpf={cpfUsuario}
                          />
                        ) : (
                          <div className="text-muted-foreground">CPF do usuário não encontrado</div>
                        )}
                      </div>
                    </DialogContent>
                  </Dialog>

                  {/* Botões de Aprovação/Reprovação - Mostra para todos os documentos se tiver permissão */}
                  {canWrite && (
                    <>
                      <Button 
                        size="sm" 
                        onClick={() => aprovarDocumento(documento.id)}
                        disabled={processing || documento.retorno_documento === 1}
                        className={documento.retorno_documento === 1 ? "bg-green-600 opacity-50" : "bg-green-600 hover:bg-green-700"}
                      >
                        <Check className="h-4 w-4 mr-1" />
                        {documento.retorno_documento === 1 ? 'Aprovado' : 'Aprovar'}
                      </Button>

                      <Dialog>
                        <DialogTrigger asChild>
                          <Button 
                            variant="destructive" 
                            size="sm"
                            onClick={() => setSelectedDocument(documento)}
                            disabled={processing || documento.retorno_documento === 0}
                            className={documento.retorno_documento === 0 ? "opacity-50" : ""}
                          >
                            <X className="h-4 w-4 mr-1" />
                            {documento.retorno_documento === 0 ? 'Reprovado' : 'Reprovar'}
                          </Button>
                        </DialogTrigger>
                        <DialogContent>
                          <DialogHeader>
                            <DialogTitle>Reprovar Documento</DialogTitle>
                          </DialogHeader>
                          <div className="space-y-4">
                            <p>
                              Você está prestes a reprovar o documento: 
                              <strong> {formatTipoDocumento(documento.nome_documento)}</strong>
                            </p>
                            
                            <div>
                              <Label htmlFor="motivo">Motivo da reprovação *</Label>
                              <Textarea
                                id="motivo"
                                placeholder="Descreva o motivo da reprovação..."
                                value={rejectionReason}
                                onChange={(e) => setRejectionReason(e.target.value)}
                                className="mt-1"
                              />
                            </div>

                            <div className="flex justify-end gap-2">
                              <Button variant="outline" onClick={() => {
                                setSelectedDocument(null);
                                setRejectionReason('');
                              }}>
                                Cancelar
                              </Button>
                              <Button 
                                variant="destructive"
                                onClick={() => reprovarDocumento(documento.id)}
                                disabled={!rejectionReason.trim() || processing}
                              >
                                Confirmar Reprovação
                              </Button>
                            </div>
                          </div>
                        </DialogContent>
                      </Dialog>
                    </>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {documentos.every(d => d.retorno_documento === 1) && documentos.length > 0 && (
        <div className="mt-4 p-4 bg-green-50 border border-green-200 rounded-lg">
          <div className="flex items-center gap-2 text-green-800">
            <Check className="h-5 w-5" />
            <span className="font-medium">Todos os documentos foram aprovados!</span>
          </div>
          <p className="text-green-700 text-sm mt-1">
            O candidato foi automaticamente liberado para a etapa de exame.
          </p>
        </div>
      )}
    </div>
  );
}